import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';

import {
  FinnairBoundItem,
  FinnairCheckInVersion,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  IconPosition,
  ModalButtons,
  NotificationTheme,
} from '@fcom/ui-components';
import { isTruthy, unsubscribe } from '@fcom/core/utils';
import { MmbOrderService } from '@fcom/manage-booking-common';
import { OrderActions } from '@fcom/common-booking';
import { ElementActions, ElementTypes, GaContext, ToasterService } from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate';
import { RedirectService } from '@fcom/common-booking/modules/redirect-service/services/redirect.service';
import { GtmService } from '@fcom/common/gtm';
import { RootPaths } from '@fcom/core';
import { isBoundAPISRequired } from '@fcom/common-booking/utils/common-booking.utils';

import { ManageBookingState } from '../../../../interfaces';
import { CheckInActions } from '../../../../store';
import { CheckInService } from '../../../../services/check-in.service';

type PassengerForm = FormGroup<{ passengers: FormGroup<Record<string, FormControl<boolean>>> }>;

type TriggerMode = 'block' | 'buttons';

@Component({
  selector: 'fin-check-in-trigger',
  templateUrl: 'check-in-trigger.component.html',
  styleUrls: ['./check-in-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInTriggerComponent implements OnInit, OnDestroy, OnChanges {
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  order: FinnairOrder;

  @Input()
  bound: FinnairBoundItem;

  @Input()
  autoOpenCheckIn = false;

  @Input()
  clearSeatsBeforeCheckIn = false;

  @Input()
  skipTravelReady = false;

  @Input()
  mode: TriggerMode = 'buttons';

  canUseCheckIn = false;
  hasCheckedInCustomers = false;
  showBoardingPassButton = false;
  allCustomersHasCheckedIn = false;
  isBoundApisRequired = false;
  checkinForm: PassengerForm;
  checkInModalClosed$: Subject<boolean> = new Subject<boolean>();
  cancelCheckinForm: PassengerForm;
  cancelCheckInPassengerModalOpen = false;
  cancelCheckInModalClosed$: Subject<boolean> = new Subject<boolean>();
  modifyCheckinForm: PassengerForm;
  passengersWithCheckinInfo: (FinnairPassengerItem & {
    isCheckedIn: boolean;
    isAllowedToUseCheckIn: boolean;
    isAllowedToUseBoardingPass: boolean;
    withInfant: string | null;
  })[];
  eligibleFlightIds: string[];
  cancelCheckInInProgress = false;
  isLegacyVersion: boolean;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<ManageBookingState>,
    private router: Router,
    private mmbOrderService: MmbOrderService,
    private toasterService: ToasterService,
    private languageService: LanguageService,
    private redirectService: RedirectService,
    private gtmService: GtmService,
    private cdr: ChangeDetectorRef,
    private checkInService: CheckInService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();

    if (this.autoOpenCheckIn) {
      this.redirectToCheckIn();
    }
    this.isBoundApisRequired = isBoundAPISRequired(this.order, this.bound);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['order']?.firstChange === false || changes['mode']?.firstChange === false) {
      this.initializeComponent();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  redirectToCheckIn(): void {
    if (!this.skipTravelReady && this.isEligibleForTravelReady()) {
      this.router.navigateByUrl(`${this.languageService.langValue}/${RootPaths.TRAVEL_READY}`);
    } else if (this.isLegacyVersion) {
      this.trackElement('check-in-legacy');
      this.redirectService.redirectToCheckIn(this.order, this.bound);
    } else {
      this.trackElement('check-in-nextgen-funnel');
      this.redirectToCheckInFunnel();
    }
  }

  private redirectToCheckInFunnel(): void {
    if (this.clearSeatsBeforeCheckIn) {
      this.checkInService.setClearPendingSeatsOnEntry(true);
    }
    this.checkInService.setModifyCheckIn(false);
    this.store$.dispatch(CheckInActions.setBoundId({ boundId: this.bound.id }));
    this.initializeCheckIn();
  }

  redirectToModifyCheckIn(): void {
    this.trackElement('modify');
    this.navigateToModifyCheckInFunnel(
      this.passengersWithCheckinInfo.map(({ id }) => id),
      this.bound.id
    );
  }

  redirectToBoardingPass(): void {
    this.store$.dispatch(CheckInActions.setBoundId({ boundId: this.bound.id }));
    this.store$.dispatch(
      CheckInActions.setPassengers({
        passengers: this.getPassengerIdsWithInfants(
          this.passengersWithCheckinInfo
            .filter((p) => p.isCheckedIn || p.isAllowedToUseBoardingPass)
            .map(({ id }) => id)
        ),
      })
    );
    this.trackElement('boarding-pass');
    this.router.navigateByUrl(`${this.languageService.langValue}/manage/check-in/boarding-pass`);
  }

  redirectToCancelCheckIn(): void {
    this.trackElement('cancel-check-in');
    this.cancelCheckInPassengerModalOpen = true;
  }

  cancelCheckIn(): void {
    this.trackElement('cancel-check-in-modal_cancel-check-in', ElementTypes.MODAL);
    this.cancelCheckinForm.disable();
    const passengerIds = Object.entries(this.cancelCheckinForm.get('passengers').value)
      .filter(([, value]) => value)
      .map(([key]) => key);
    this.cancelCheckInForPassengers(passengerIds);
  }

  closeCancelModal(): void {
    this.cancelCheckInPassengerModalOpen = false;
    this.trackElement('cancel-check-in-modal_cancel', ElementTypes.MODAL);
  }

  private trackElement(elementNameSuffix: string, elementType: ElementTypes = ElementTypes.BUTTON): void {
    this.gtmService.trackElement(
      `check-in-trigger_${elementNameSuffix}`,
      GaContext.CHECK_IN,
      elementType,
      undefined,
      ElementActions.CLICK
    );
  }

  private isEligibleForTravelReady(): boolean {
    return this.order.eligibilities?.travelReady?.find((e) => e.id === this.bound.id)?.isAllowedToUse ?? false;
  }

  private initializeComponent(): void {
    const checkInEligibilityForBound = this.order?.eligibilities?.checkIn?.find(
      (eligibility) => eligibility.id === this.bound?.id
    );

    this.canUseCheckIn = checkInEligibilityForBound?.isAllowedToUseCheckIn ?? false;
    this.isLegacyVersion = checkInEligibilityForBound?.version !== FinnairCheckInVersion.NEXTGEN;

    const checkinFlightEligibilities = checkInEligibilityForBound?.flightTravelerEligibility ?? {};

    this.hasCheckedInCustomers = Object.values(checkinFlightEligibilities).some((flightCheckinEligibility) =>
      flightCheckinEligibility.some((eligibility) => eligibility.isCheckedIn)
    );

    this.showBoardingPassButton =
      this.hasCheckedInCustomers ||
      Object.values(checkinFlightEligibilities).some((flightCheckinEligibility) =>
        flightCheckinEligibility.some((eligibility) => eligibility.isAllowedToUseBoardingPass)
      );

    this.allCustomersHasCheckedIn = Object.values(checkinFlightEligibilities).some((flightCheckinEligibility) =>
      flightCheckinEligibility.every((eligibility) => eligibility.isCheckedIn)
    );

    this.eligibleFlightIds = Object.entries(checkinFlightEligibilities).reduce((all, [flightId, flightEligibility]) => {
      if (flightEligibility.some((e) => e.isAllowedToUseCheckIn)) {
        all.push(flightId);
      }
      return all;
    }, []);

    this.passengersWithCheckinInfo = this.order.passengers
      .map((passenger) => {
        // TODO: This does not take into account multi flight different eligibilities
        const passengerEligibility = Object.values(checkinFlightEligibilities)
          .flatMap((flightCheckinEligibility) =>
            flightCheckinEligibility.find((eligibility) => eligibility.travelerIds.includes(passenger.id))
          )
          .filter(Boolean)[0];

        return {
          ...passenger,
          withInfant: this.getInfantAssociation(this.order.passengers, passenger),
          isCheckedIn: passengerEligibility?.isCheckedIn ?? false,
          isAllowedToUseCheckIn: passengerEligibility?.isAllowedToUseCheckIn ?? false,
          isAllowedToUseBoardingPass: passengerEligibility?.isAllowedToUseBoardingPass ?? false,
        };
      })
      .filter(({ passengerTypeCode }) => passengerTypeCode !== FinnairPassengerCode.INF);

    this.checkinForm = this.createForm(this.passengersWithCheckinInfo, (passenger) => {
      return new FormControl({
        value: !passenger.isCheckedIn,
        disabled: passenger.isCheckedIn || !passenger.isAllowedToUseCheckIn,
      });
    });

    this.cancelCheckinForm = this.createForm(this.passengersWithCheckinInfo, (passenger) => {
      return new FormControl({ value: false, disabled: !passenger.isCheckedIn || !passenger.isAllowedToUseCheckIn });
    });

    this.modifyCheckinForm = this.createForm(this.passengersWithCheckinInfo, (passenger) => {
      return new FormControl({ value: true, disabled: !passenger.isAllowedToUseCheckIn });
    });
  }

  private cancelCheckInForPassengers(passengerIds: string[]): void {
    this.cancelCheckInInProgress = true;
    this.subscriptions.add(
      this.mmbOrderService
        .cancelCheckIn(this.order.id, this.order.hash, {
          flightIds: this.eligibleFlightIds,
          travelerIds: this.getPassengerIdsWithInfants(passengerIds),
        })
        .subscribe({
          next: (order) => {
            this.cancelCheckinForm.enable();
            this.store$.dispatch(OrderActions.setOrderData({ order }));
            this.cancelCheckInPassengerModalOpen = false;
            this.cancelCheckInInProgress = false;
            this.toasterService.addMessageToQueue({
              id: 'cancelCheckIn',
              theme: NotificationTheme.SUCCESS,
              iconName: SvgLibraryIcon.CHECKMARK_BLOCK,
              contentKey: 'MMB.checkIn.passengerSelection.cancel.notification.success',
            });
          },
          error: () => {
            this.toasterService.addMessageToQueue({
              id: 'cancelCheckIn',
              theme: NotificationTheme.ALERT,
              iconName: SvgLibraryIcon.ALERT_TRIANGLE,
              contentKey: 'MMB.checkIn.passengerSelection.cancel.notification.error',
            });
            this.cancelCheckinForm.enable();
            this.cancelCheckInPassengerModalOpen = false;
            this.cancelCheckInInProgress = false;
          },
        })
    );
  }

  private getInfantAssociation(passengers: FinnairPassengerItem[], passenger: FinnairPassengerItem): string | null {
    const infant = passengers.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passenger.id);
    if (infant) {
      return `${infant.firstName} ${infant.lastName}`;
    }
    return null;
  }

  private getPassengerIdsWithInfants(passengerIds: string[]): string[] {
    return this.order.passengers
      .filter((passenger) =>
        passengerIds.some(
          (passengerId) => passenger.id === passengerId || passenger.accompanyingTravelerId === passengerId
        )
      )
      .map(({ id }) => id);
  }

  private navigateToModifyCheckInFunnel(passengerIds: string[], boundId: string): void {
    this.store$.dispatch(CheckInActions.setBoundId({ boundId }));
    this.store$.dispatch(CheckInActions.setPassengers({ passengers: this.getPassengerIdsWithInfants(passengerIds) }));
    this.checkInService.setModifyCheckIn(true);
    this.initializeCheckIn();
  }

  private initializeCheckIn(): void {
    this.mmbOrderService.initializeCheckIn(this.order.id, this.order.hash, { flightIds: this.eligibleFlightIds });
    this.router.navigateByUrl(`${this.languageService.langValue}/manage/check-in/step/passengers`);
  }

  private createForm(
    passengers: (FinnairPassengerItem & {
      isCheckedIn: boolean;
      isAllowedToUseCheckIn: boolean;
    })[],
    createFormControl: (passenger) => FormControl<boolean>
  ): PassengerForm {
    const hasPassengersSelected = (control: AbstractControl) => {
      if (!Object.values(control.get('passengers').value).some(isTruthy)) {
        return { custom: true };
      }
      return null;
    };

    return new FormGroup(
      {
        passengers: new FormGroup(
          passengers.reduce(
            (all, passenger) => {
              all[passenger.id] = createFormControl(passenger);
              return all;
            },
            {} as Record<string, FormControl>
          )
        ),
      },
      hasPassengersSelected
    );
  }
}
